import { useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

const MIN_COLUMN_WIDTH = 72;

/**
 * Per-surface column widths, persisted to localStorage so a drag survives
 * reloads and view switches. Column widths are a device-level display
 * preference, not a shared/saved-view setting — kept out of DataViewState on
 * purpose. Keyed by a stable column id; unknown/garbage values fall back to the
 * defaults so a schema change never wedges the table.
 */
export function useColumnWidths(storageKey: string, defaults: Readonly<Record<string, number>>) {
  const [widths, setWidths] = useState<Record<string, number>>(() =>
    readWidths(storageKey, defaults),
  );

  const commit = (next: Record<string, number>) => {
    setWidths(next);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Private mode / quota — resizing still works for the session.
      }
    }
  };

  const setColumnWidth = (id: string, px: number) =>
    setWidths((prev) => {
      const next = { ...prev, [id]: Math.max(MIN_COLUMN_WIDTH, Math.round(px)) };
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          /* ignore */
        }
      }
      return next;
    });

  const resetColumn = (id: string) => commit({ ...widths, [id]: defaults[id] ?? MIN_COLUMN_WIDTH });

  return { widths, setColumnWidth, resetColumn };
}

function readWidths(
  storageKey: string,
  defaults: Readonly<Record<string, number>>,
): Record<string, number> {
  if (typeof window === "undefined") return { ...defaults };
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return { ...defaults };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const next: Record<string, number> = { ...defaults };
    for (const id of Object.keys(defaults)) {
      const value = parsed[id];
      if (typeof value === "number" && Number.isFinite(value) && value >= MIN_COLUMN_WIDTH) {
        next[id] = value;
      }
    }
    return next;
  } catch {
    return { ...defaults };
  }
}

/**
 * Drag handle for a table `<th>`. Absolutely positioned on the column's right
 * edge; the parent `<th>` must be `relative`. Drag to resize, double-click to
 * reset. Reuses the pointer-capture pattern from the timeline work column.
 */
export function ColumnResizeHandle({
  width,
  onWidthChange,
  onReset,
  ariaLabel,
}: {
  readonly width: number;
  readonly onWidthChange: (px: number) => void;
  readonly onReset: () => void;
  readonly ariaLabel: string;
}) {
  // useRef, not a plain object: the parent re-renders on every width change
  // during a drag, so the in-flight drag state must survive re-render.
  const start = useRef<{ x: number; width: number } | null>(null);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    start.current = { x: event.clientX, width };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!start.current) return;
    onWidthChange(start.current.width + (event.clientX - start.current.x));
  };
  const end = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!start.current) return;
    start.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      title="Drag to resize · double-click to reset"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={onReset}
      onClick={(event) => event.stopPropagation()}
      className="group/resize absolute inset-y-0 right-0 z-20 flex w-2 translate-x-1/2 cursor-col-resize touch-none items-stretch justify-center"
    >
      <span className="h-full w-px bg-transparent transition-colors group-hover/resize:bg-primary/60 group-active/resize:bg-primary" />
    </div>
  );
}
